import { Box, Stack, Text, Tooltip as MantineTooltip, useMantineTheme } from '@mantine/core';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getDemographicColor, getDemographicLabel } from '../../demographics';
import type { ThecData } from '../types';
import { latest, pctChange, previous } from '../helpers';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

export function ProgramParticipantsCard({ data }: { data: ThecData }) {
  const theme = useMantineTheme();
  const series = data.participants;
  const cur = series.length > 0 ? latest(series) : null;
  const prev = previous(series);

  const allTime = data.participantsAllTimeTotal;
  const qoq = cur ? pctChange(cur.participantsStarted, prev?.participantsStarted ?? null) : null;
  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqText = qoq === null ? '—' : arrow(qoq) ? `${arrow(qoq)}${Math.abs(qoq).toFixed(2)}% QoQ` : '0.00% QoQ';

  const parseQuarterLabel = (label: string): { year: number; quarter: 1 | 2 | 3 | 4 } | null => {
    const m1 = label.match(/^Q([1-4])\s+(\d{4})$/);
    if (m1) return { year: Number(m1[2]), quarter: Number(m1[1]) as 1 | 2 | 3 | 4 };
    const m2 = label.match(/^(\d{4})\s+Q([1-4])$/);
    if (m2) return { year: Number(m2[1]), quarter: Number(m2[2]) as 1 | 2 | 3 | 4 };
    return null;
  };

  const curMeta = parseQuarterLabel(cur?.quarter ?? '');
  const curYear = curMeta?.year ?? null;
  const curQuarterNum = curMeta?.quarter ?? null;

  const ytd =
    curYear === null || curQuarterNum === null
      ? null
      : series
          .filter((p) => {
            const m = parseQuarterLabel(p.quarter);
            return m?.year === curYear && m.quarter <= curQuarterNum;
          })
          .reduce((acc, p) => acc + p.participantsStarted, 0);

  const prevYearYtd =
    curYear === null || curQuarterNum === null
      ? null
      : series
          .filter((p) => {
            const m = parseQuarterLabel(p.quarter);
            return m?.year === curYear - 1 && m.quarter <= curQuarterNum;
          })
          .reduce((acc, p) => acc + p.participantsStarted, 0);

  const yoyPct = ytd === null || prevYearYtd === null ? null : pctChange(ytd, prevYearYtd);
  const yoyText = yoyPct === null ? '—' : arrow(yoyPct) ? `${arrow(yoyPct)}${Math.abs(yoyPct).toFixed(2)}% YoY` : '0.00% YoY';

  const status = { label: 'Snapshot', color: 'gray' as const };

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  const genderTotals = data.demographicsAllTime.byGender;
  const genderOrder = Object.keys(genderTotals);
  const genderSegments = genderOrder.map((gender, idx) => ({
    key: gender,
    label: getDemographicLabel(gender),
    total: genderTotals[gender] ?? 0,
    color: getDemographicColor(theme, idx),
  }));
  const genderTotal = genderSegments.reduce((acc, s) => acc + s.total, 0);

  return (
    <CardShell title="Program Participants" subtitle={cur?.quarter ?? 'No data'} statusLabel={status.label} statusColor={status.color}>
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: 'Unique individuals who began participation this quarter.',
              value: (
                <Text size="lg" weight={800}>
                  {cur?.participantsStarted ?? '—'}
                </Text>
              ),
              sublabel: qoqText,
            },
            {
              label: 'YTD',
              labelTooltip: 'Year-to-date participants (sum of quarters in the current year through this quarter).',
              value: (
                <Text size="lg" weight={800}>
                  {ytd === null ? '—' : ytd}
                </Text>
              ),
              sublabel: yoyText,
            },
            {
              label: 'All-Time',
              labelTooltip: 'Total participants all-time (includes periods not shown in the trend window).',
              value: (
                <Text size="lg" weight={800}>
                  {allTime}
                </Text>
              ),
            },
          ]}
        />

        <Stack spacing={6}>
          <MantineTooltip
            withArrow
            withinPortal
            label="All-time participant share by gender."
            styles={{
              tooltip: {
                ...(tooltipShellStyle as any),
                maxWidth: 260,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                lineHeight: 1.35,
              },
              arrow: { border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}` },
            }}
          >
            <Text
              size="xs"
              color="dimmed"
              sx={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}
            >
              Participants By Gender (All-Time)
            </Text>
          </MantineTooltip>

          <Box
            sx={{
              height: 10,
              borderRadius: 999,
              overflow: 'hidden',
              background: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[2],
              display: 'flex',
            }}
          >
            {(() => {
              const widths = genderSegments.map((seg) => {
                const pct = genderTotal > 0 ? (seg.total / genderTotal) * 100 : 0;
                return Math.max(0, Math.min(100, pct));
              });
              const lastVisibleIndex = widths.reduce((acc, w, i) => (w > 0 ? i : acc), -1);
              return genderSegments.map((seg, idx) => {
                const pct = widths[idx] ?? 0;
                const isFirst = idx === 0;
                const isLast = idx === lastVisibleIndex;
                const radius = isFirst && isLast ? 999 : isFirst ? '999px 0 0 999px' : isLast ? '0 999px 999px 0' : 0;
                return (
                  <MantineTooltip
                    key={seg.key}
                    withArrow
                    withinPortal
                    label={`${seg.label}: ${seg.total} · ${Math.round(pct)}%`}
                    styles={{
                      tooltip: {
                        ...(tooltipShellStyle as any),
                        maxWidth: 200,
                        whiteSpace: 'normal',
                        lineHeight: 1.35,
                      },
                      arrow: { border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}` },
                    }}
                  >
                    <Box
                      sx={{
                        width: `${pct}%`,
                        background: seg.color,
                        borderRadius: radius as any,
                        cursor: pct > 0 ? 'default' : 'inherit',
                      }}
                    />
                  </MantineTooltip>
                );
              });
            })()}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px 12px',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {genderSegments.map((seg) => (
              <Box key={`legend-${seg.key}`} sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: seg.color,
                    flex: '0 0 auto',
                  }}
                />
                <Text size="xs" color="dimmed">
                  {seg.label}
                </Text>
              </Box>
            ))}
          </Box>
        </Stack>

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Quarterly Participants Started
        </Text>

        <Box sx={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
              <XAxis dataKey="quarter" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
              <YAxis orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} axisLine={false} tickLine={false} width={44} tickMargin={8} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const v = payload[0]?.value;
                  return (
                    <div style={tooltipShellStyle as any}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                      <div style={{ fontWeight: 900, fontSize: 14, lineHeight: 1.2 }}>
                        {typeof v === 'number' ? v : '—'} Participants
                      </div>
                    </div>
                  );
                }}
              />
              <Bar
                dataKey="participantsStarted"
                name="Participants"
                fill={theme.colors.blue[6]}
                radius={[6, 6, 0, 0]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </CardShell>
  );
}
